import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { InputAdornment, IconButton } from '@material-ui/core';
import TextField from '@mui/material/TextField';
import FetchBillsIcon from '@mui/icons-material/ArrowForwardRounded';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/DeleteOutlineRounded';

import Backdrop from '@material-ui/core/Backdrop';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import SecondaryButton from './lib/buttons/SecondaryButton';
import NeutralButton from './lib/buttons/NeutralButton';
import PrimaryButton from './lib/buttons/PrimaryButton';

import { Loading } from '../stories/Loading';

interface GstRecordProps {}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  button: {
    maxWidth: '320px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  guestInfoDataKey: {
    flex: 1,
    color: '#004878',
    fontSize: '18px',
    fontWeight: 400,
    padding: '8px',
    verticalAlign: 'top',
  },
  guestInfoDataValue: {
    flex: 1,
    color: '#004878',
    fontSize: '18px',
    fontWeight: 700,
    padding: '8px',
    verticalAlign: 'top',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  particularsTable: {
    marginTop: '12px',
    marginBottom: '12px',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
      alignSelf: 'center',
    },
  },
  deleteIcon: {
    justifyContent: 'center',
    fontSize: 32,
    '&:hover': {
      color: '#c23616',
    },
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 16,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const GstRecord = (props: GstRecordProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const [gstin, setGstin] = useState<string>();
  const [gstInfo, setGstInfo] = useState<any>();
  const [gstMappings, setGstMappings] = useState<Array<any>>([]);
  const [showForceFetch, setShowForceFetch] = useState<boolean>(false);

  useEffect(() => getAllGstMappings(), []);

  const getAllGstMappings = () => {
    axios.get('/api/fetchGstMappings').then(({ data }) => {
      setGstMappings(data);
    });
  };

  const fetchGstInfo = () => {
    if (gstin && gstin.length === 15) {
      setLoading(true);
      axios
        .get(`/api/lookupGstInfo?gstNumber=${gstin}`)
        .then(({ data }) => {
          if (data.errno === 404) {
            if (data.shouldShowForceFetch) {
              setShowForceFetch(true);
            } else {
              alert('GST Number Not Found!');
            }
          } else {
            setGstInfo(data.gstInfo);
          }
        })
        .catch((e) => {
          alert('Error fetching data');
          console.log(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const fetchAndStoreGstInfo = () => {
    setLoading(true);
    axios
      .post(`/api/storeGstInfo`, {
        gstNumber: gstin,
      })
      .then(({ data }) => {
        if (data.success) {
          setGstInfo(data.gstInfo);
        } else {
          alert(data.msg);
          console.log(data.msg);
        }
      })
      .catch((e) => {
        alert('Error fetching data');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
        setShowForceFetch(false);
      });
  };

  const saveGstRecord = () => {
    setLoading(true);
    axios
      .post(`/api/createGstMapping`, {
        gstNumber: gstin,
      })
      .then(({ data }) => {
        if (data.success) {
          getAllGstMappings();
        } else {
          alert(data.msg);
          console.log(data.msg);
        }
      })
      .catch((e) => {
        alert('Error fetching data');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteSavedGstRecord = (gstin: string) => {
    setLoading(true);
    axios
      .post(`/api/deleteSavedGstRecord`, {
        gstNumber: gstin,
      })
      .then(({ data }) => {
        if (data.success) {
          getAllGstMappings();
        } else {
          alert(data.msg);
          console.log(data.msg);
        }
      })
      .catch((e) => {
        alert('Error fetching data');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>GST Records</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <Backdrop className={classes.backdrop} open={showForceFetch}>
            <Dialog
              open={showForceFetch}
              onClose={() => setShowForceFetch(false)}
              aria-labelledby="form-dialog-title"
            >
              <DialogTitle id="form-dialog-title">
                Force Fetch GST Info
              </DialogTitle>
              <DialogActions>
                <NeutralButton
                  onClick={() => setShowForceFetch(false)}
                  className={classes.button}
                  style={{ marginTop: '8px' }}
                >
                  NO
                </NeutralButton>
                <SecondaryButton
                  onClick={(_: any) => fetchAndStoreGstInfo()}
                  color="secondary"
                  className={classes.button}
                  style={{ marginTop: '8px' }}
                >
                  YES
                </SecondaryButton>
              </DialogActions>
            </Dialog>
          </Backdrop>
          <div className={classes.formContainer}>
            <TextField
              label="GSTIN"
              variant="outlined"
              value={gstin}
              onChange={(e) => {
                setGstInfo(undefined);
                setGstin(e.target.value.toUpperCase());
              }}
              onKeyDown={(key) => {
                if (key.key === 'Enter') {
                  fetchGstInfo();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={fetchGstInfo}>
                      <FetchBillsIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              style={{
                width: 300,
                marginTop: '32px',
              }}
            />
          </div>
          <hr className={classes.formSeparator} />
          <div className={classes.formContainer}>
            <label
              style={{
                margin: '8px',
                marginBottom: '32px',
                textDecoration: 'underline',
              }}
            >
              Company Details
            </label>
            <table style={{ flex: 1 }}>
              <tbody>
                <tr>
                  <td className={classes.guestInfoDataKey}>GSTIN:</td>
                  <td className={classes.guestInfoDataValue}>
                    {gstInfo?.Gstin}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Legal Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {gstInfo?.LegalName}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Trade Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {gstInfo?.TradeName}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Address:</td>
                  <td className={classes.guestInfoDataValue}>
                    {`${gstInfo?.AddrBno ? gstInfo?.AddrBno + ',' : ''} ${
                      gstInfo?.AddrFlno ? gstInfo.AddrFlno + ',' : ''
                    } ${gstInfo?.AddrSt ? gstInfo?.AddrSt : ''}`}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Location:</td>
                  <td className={classes.guestInfoDataValue}>
                    {`${gstInfo?.AddrLoc ? gstInfo?.AddrLoc : ''}`}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Pincode:</td>
                  <td className={classes.guestInfoDataValue}>
                    {gstInfo?.AddrPncd}
                  </td>
                </tr>
              </tbody>
            </table>
            <PrimaryButton onClick={saveGstRecord} className={classes.button}>
              Save
            </PrimaryButton>
          </div>
        </div>
        <hr className={classes.separatorLine} />
        <TableContainer component={Paper} className={classes.particularsTable}>
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>GSTIN</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Loc</StyledTableCell>
                <StyledTableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {gstMappings.map((row) => (
                <StyledTableRow key={row?.gstin}>
                  <StyledTableCell>{row?.gstin}</StyledTableCell>
                  <StyledTableCell>{row?.legal_name}</StyledTableCell>
                  <StyledTableCell>{row?.location}</StyledTableCell>
                  <StyledTableCell
                    align="right"
                    style={{
                      justifyItems: 'center',
                    }}
                  >
                    <DeleteIcon
                      onClick={(_) => deleteSavedGstRecord(row?.gstin)}
                      className={classes.deleteIcon}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }
};

export default GstRecord;
